// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import {
	computed,
	ref,
	watch,
	provide,
	onMounted,
	onBeforeUnmount,
	type VNodeRef,
} from 'vue'
import axios from 'axios'
import { useStore } from '@nanostores/vue'
import { isMobile } from '@/legacy/ts/checkDevice'
import {
	useBreakpoints,
	getVueAssetImagePath,
	capitalizeFirstLetter,
	setCurrentGaPageName,
} from '@/lib/utils'
import hybridPage from '@/lib/hybridPage'
import User from '@/legacy/ts/user'
import {
	getProductSharingUrl,
	checkProductSharingEnable,
	checkExpressCheckoutEnable,
	productSharingData,
	productSharingPostPerPage,
	productSharingPostColumn,
	getPostListModeMapping,
	type ProductSharingUrlType,
	postListModeMappings,
	getSecondaryTabGroupNameBySlug,
} from '@/legacy/ts/productSharing'
import useHashQuery from '@/legacy/ts/useHashQuery'
import useSnackBar from '@/legacy/ts/useSnackBar'
import usePostCard from '@/legacy/ts/usePostCard'
import useProductSharing from '@/legacy/ts/useProductSharing'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import BsLoading from '@/legacy/components/Loading.vue'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import ProductSharingSearchBar from '@/legacy/components/ProductSharingSearchBar.vue'
import ProductSharingPostList, {
	type IWarehouseList,
} from '@/legacy/components/ProductSharingPostList.vue'
import ProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import CommunityBanner from '@/components/CommunityBanner.vue'
import FilterButton from '@/components/FilterButton.vue'
import CommunityTabs from '@/components/CommunityTabs.vue'
import BackButton from '@/components/BackButton.vue'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'
import {
	fetchProductSharingSettings,
	instanceBuyandship,
} from '@/assets/ts/apiManagement'
import {
	setTabsListStructure,
	setTabsListDetail,
	$validRouteList,
	getSlugByRoute,
} from '@/store/productSharingStore'

const validRouteList = useStore($validRouteList)

hybridPage()

type BannerTab = {
	key: 'discover' | 'mall'
	label: string
	icon: string
	iconFill: string
	tabs: string[]
}

createApp({
	name: 'Product-Sharing-Index',
	components: {
		BsLoading,
		BsSnackBar,
		BsDialogAction,
		ProductSharingSearchBar,
		ProductSharingPostList,
		ProductSharingPostCard,
		CommunityBanner,
		FilterButton,
		CommunityTabs,
		BackButton,
	},
	setup() {
		setCurrentGaPageName(GaGeneralPageName.CommunityPostListingPage)
		const { postCardTextInfo } = window
		const { postListTextInfo } = window
		const { textService } = window.pageContent
		const { sortingOptions } = window.pageContent
		const { bannerContent } = window.pageContent
		const {
			secondaryTabs: secondaryTabsFromPageContent,
		}: { secondaryTabs: { label: string; value: string }[] } =
			window.pageContent
		const { searchbarTextInfo } = window.pageContent
		const bannerTabs = ref<BannerTab[]>([
			{
				key: 'discover',
				label: bannerContent.discover,
				icon: 'post-discover',
				iconFill: 'post-discover-fill',
				tabs: [],
			},
			{
				key: 'mall',
				label: bannerContent.mall,
				icon: 'mall',
				iconFill: 'mall-fill',
				tabs: [],
			},
		])
		const formattedCurrentMode = computed(() => {
			const mode = getSecondaryTabGroupNameBySlug(
				getSlugByRoute(currentMode.value),
			)
			return mode || currentMode.value
		})
		const { isMedium, isLarge, isTabletLandscape } = useBreakpoints()
		const displayedBannerTabs = computed(() => {
			return bannerTabs.value.filter(tab => tab.tabs.length > 0)
		})
		const currentBannerTab = ref<BannerTab['key']>('discover')
		const isShowClose = ref(false)
		const backToTopObserveElement = ref<HTMLElement | null>(null)
		const bannerObserver = ref<IntersectionObserver | null>(null)
		const { isShow, messages, dismiss, addMessage } = useSnackBar()
		const postListRef = ref<VNodeRef | null>(null)
		const {
			postDetailCurrent,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			DialogType,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			interestsList,
			hideDialogPostDetail,
			setPostActions,
			fetchSettingInterest,
			setSupportedCurrencies,
		} = usePostCard(postListRef)
		const { isHasPSProfile, PSUsername, fetchPSUserProfile, PSUserAvatarSrc } =
			useProductSharing()
		const { searchParameter } = useHashQuery()
		const currentSecondaryTabs = computed(() => {
			const currentTab = bannerTabs.value.find(
				tab => tab.key === currentBannerTab.value,
			)
			if (!currentTab || !secondaryTabsFromPageContent) {
				return []
			}

			return secondaryTabsFromPageContent
				.filter(tab => {
					return currentTab.tabs.includes(tab.value)
				})
				.map(tab => ({
					...tab,
					route: getPostListModeMapping('name')[tab.value],
				}))
		})
		const currentSecondaryTab = ref('')
		const currentWarehouseAlias = ref('')
		const currentCategoryAlias = ref('')

		provide('addMessage', addMessage)

		const user = new User()
		const userProgress = ref(null)
		const featureEnable = ref(checkProductSharingEnable())
		const isLoading = ref(true)
		const isExpressCheckoutEnable = ref(checkExpressCheckoutEnable())
		const localCurrencyInfo = ref(null)
		const defaultSorting = ref('recommended')
		const searchFormParent = ref<VNodeRef | null>(null)
		const isFocus = ref(false)
		const shouldOpenFilter = ref(false)
		const filterCount = ref(0)
		const sortMode = ref('recommended')

		const columnCount = computed(() => {
			if (isLarge.value) return productSharingPostColumn.SizeLG
			if (isTabletLandscape.value) return productSharingPostColumn.SizeMDL
			if (isMedium.value) return productSharingPostColumn.SizeMDP
			return productSharingPostColumn.SizeSM
		})

		const currentMode = ref('')

		const updateCurrentMode = () => {
			const url = new URL(window.location.href)
			if (!import.meta.env.PROD) {
				const params = new URLSearchParams(url.search)
				const modeParam = params.get('mode')

				// If going forward to post detail page, the mode param will be null
				// Only update currentMode when modeParam is not null to prevent unnecessary re-freshing
				if (modeParam !== null) {
					currentMode.value = modeParam
				}
				return
			}

			const lastSegment = url.pathname.split('/').filter(Boolean).at(-1)
			if (lastSegment) {
				currentMode.value = lastSegment
			}
		}

		const isCurrentModeDefault = computed(
			() =>
				formattedCurrentMode.value === 'recommended' ||
				formattedCurrentMode.value === 'hot',
		)
		const isHidePSMemberAccess = computed(() => {
			return (
				!productSharingData.isLogin ||
				(userProgress.value ?? 0) < 2 ||
				!PSUsername.value
			)
		})
		const subMenuList = computed(() => {
			return isHidePSMemberAccess.value
				? window.pageContent.linkInfo?.filter(
						(item: any) =>
							item.name !== 'discover' && item.name !== 'private-profile',
				  )
				: window.pageContent.linkInfo
		})
		const menuStyleClass = computed(() =>
			isHidePSMemberAccess.value
				? 'bs-product-sharing-index__menu--end'
				: 'bs-product-sharing-index__menu--space',
		)

		const isDemoApi = computed(() => {
			const mode = searchParameter.value.find(query => query.key === 'demo')
			return !!(mode && mode.val)
		})
		const currentSearchParameter = computed(() => {
			const q = searchParameter.value.find(query => query.key === 'q')
			const hashtag = searchParameter.value.find(
				query => query.key === 'hashtag',
			)
			const result =
				(q && decodeURIComponent(q.val)) ||
				(hashtag && decodeURIComponent(hashtag.val))
			return result || ''
		})
		const defaultAvatar = computed(() =>
			getVueAssetImagePath('avatar_2_48px@2x.png'),
		)

		function scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		}

		const currentModeSlug = computed(() => {
			return getSlugByRoute(currentMode.value)
		})

		const setBannerTab = (tab: BannerTab['key']) => {
			if (currentBannerTab.value === tab) return

			currentBannerTab.value = tab
			setSecondaryTab(currentSecondaryTabs.value[0]?.route || '')
		}

		const setSecondaryTab = (tab: string) => {
			if (!tab) return

			currentSecondaryTab.value = tab
			const url = getProductSharingUrl('mode', {
				value: currentSecondaryTab.value,
			})
			window.history.pushState({ tab, disableReplaceState: true }, '', url)
			updateCurrentMode()
		}

		function toPage(name: ProductSharingUrlType, eventName: string) {
			if (eventName) createGAEvent(eventName)
			if (name === 'search') return
			if (name === 'private-profile' || name === 'discover') {
				if (!productSharingData.isLogin) {
					window.BsHeaderVm.showPanel('login')
					return
				}
				isHasPSProfile().then(isProfileExist => {
					if (!isProfileExist)
						window.location.href = getProductSharingUrl('private-index')
					else window.location.href = getProductSharingUrl(name)
				})
			} else window.location.href = getProductSharingUrl(name)
		}

		function toPrivateProfilePage() {
			const profileData = window.pageContent.linkInfo.filter(
				(item: any) => item.name === 'private-profile',
			)
			toPage(profileData[0].name, profileData[0].eventName)
		}

		function searchFormFocus() {
			searchFormParent.value?.formFocus()
		}

		async function fetchSecondaryTabs() {
			try {
				const data = await fetchProductSharingSettings()
				if (!data) return
				const { tabsListStructure, tabsListDetail } = data
				setTabsListStructure(tabsListStructure)
				setTabsListDetail(tabsListDetail)

				if (!validRouteList.value.includes(currentMode.value)) {
					window.location.href = getProductSharingUrl('recommended')
					return
				}

				const reverseMapping = Object.fromEntries(
					Object.entries(postListModeMappings.value).map(([key, value]) => [
						value.api,
						key,
					]),
				)

				bannerTabs.value = bannerTabs.value.map(tab => {
					return {
						...tab,
						tabs: tabsListStructure[tab.key].map(t => {
							return reverseMapping[t] || t
						}),
					}
				})
			} catch (error) {
				console.error('Error fetching secondary tabs:', error)
			}
		}

		async function setLocalCurrencyInfo() {
			localCurrencyInfo.value = await fetchPricingCurrencyBuyforyou()
		}

		const initializeCurrentTabState = () => {
			const currentTab = bannerTabs.value.find(
				tab =>
					// work around for we can't change astro html now
					tab.tabs.includes(currentModeSlug.value) ||
					tab.tabs.includes(currentMode.value),
			)

			if (currentTab) {
				currentBannerTab.value = currentTab.key
				currentSecondaryTab.value = formattedCurrentMode.value
			}
		}

		function handlePopState() {
			updateCurrentMode()
			currentSecondaryTab.value = formattedCurrentMode.value

			const newBannerTab = bannerTabs.value.find(tab =>
				tab.tabs.includes(currentSecondaryTab.value),
			)
			if (newBannerTab) {
				currentBannerTab.value = newBannerTab.key
			}
		}

		const warehouseFlagMap = ref<any>({})
		const warehouseSettingList = ref<any[]>([])
		const warehouseIdMapForPostList = ref({})
		const warehouseList = ref<IWarehouseList[]>([])

		async function fetchWarehouseList() {
			return axios
				.get(`${window.BUYANDSHIP.settings.content_api_url}warehouses`)
				.then(({ data: { data } }) => {
					const res: any = {}
					data.content.warehouses.forEach(
						(item: { id: string; country_icon_url: string }) => {
							res[item.id] = item.country_icon_url
						},
					)
					warehouseFlagMap.value = res
				})
		}

		function fetchSettingWarehouses() {
			return new Promise((resolve, reject) => {
				const locale = import.meta.env.PROD ? '' : 'zh_TW'
				instanceBuyandship
					.get(`settings/${locale}?k[]=warehouses`)
					.then(({ data }) => {
						const warehouses = data.warehouses || null
						const map: any = {}
						const res: any = []
						if (warehouses) {
							Object.keys(warehouses).forEach(id => {
								map[id] = {
									alias: warehouses[id].alias,
									currency: warehouses[id].currency,
								}
								res.push({
									id,
									name: warehouses[id].name,
									alias: warehouses[id].alias,
								})
							})
						}
						warehouseIdMapForPostList.value = map
						warehouseSettingList.value = res
						resolve(true)
					})
					.catch(reject)
			})
		}

		function setWarehouseFlag() {
			const temp = JSON.parse(JSON.stringify(warehouseSettingList.value))
			temp.forEach((item: any) => {
				item.countryIconUrl = warehouseFlagMap.value[item.id]
			})
			warehouseList.value = temp
		}

		onMounted(async () => {
			initialBackTopButtonObserver()
			updateCurrentMode()

			try {
				if (featureEnable.value) {
					const userSummary = await user.getUserSummaryIdle()
					await fetchSecondaryTabs()
					initializeCurrentTabState()

					if (!(userSummary && userSummary.msg))
						userProgress.value = userSummary
					await Promise.all([
						fetchSettingInterest(),
						isExpressCheckoutEnable.value && setLocalCurrencyInfo(),
						productSharingData.isLogin &&
							(userProgress.value ?? 0) >= 2 &&
							fetchPSUserProfile(),
						setSupportedCurrencies(),
						fetchWarehouseList(),
						fetchSettingWarehouses(),
					])

					setWarehouseFlag()

					// Only for demo
					if (isDemoApi.value) {
						isHasPSProfile().then(isProfileExist => {
							if (!isProfileExist) {
								window.location.href = getProductSharingUrl('private-index')
							}
						})
					}
				}
			} catch (error) {
				//
			} finally {
				isLoading.value = false
			}

			window.addEventListener('popstate', handlePopState)
		})

		onBeforeUnmount(() => {
			clearBackTopButtonObserver()
			window.removeEventListener('popstate', handlePopState)
		})

		function initialBackTopButtonObserver() {
			bannerObserver.value = new IntersectionObserver(
				entries => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							isShowClose.value = false
						} else if (
							backToTopObserveElement.value &&
							backToTopObserveElement.value.getBoundingClientRect()?.top < 0
						) {
							isShowClose.value = true
						}
					})
				},
				{
					threshold: 1,
				},
			)

			watch(backToTopObserveElement, () => {
				if (!backToTopObserveElement.value) return
				if (bannerObserver.value) {
					bannerObserver.value.observe(backToTopObserveElement.value)
				}
			})
		}

		function clearBackTopButtonObserver() {
			if (bannerObserver.value) {
				bannerObserver.value.disconnect()
			}
		}

		function openFilter() {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.CommunityPostListingPage,
				button_name: 'Filter',
				tab_name: `${capitalizeFirstLetter(
					currentBannerTab.value,
				)}-${capitalizeFirstLetter(currentSecondaryTab.value)}`,
				sort_option: `${capitalizeFirstLetter(sortMode.value)}`,
				warehouse_filter: `${currentWarehouseAlias.value}`,
				category_filter: `${currentCategoryAlias.value}`,
				keyword: `${currentSearchParameter.value}`,
			})

			shouldOpenFilter.value = true
		}

		function formatWarehouseAlias(val: string) {
			currentWarehouseAlias.value = val.replace(/-/g, ',')
		}

		function formatCategoryAlias(val: string) {
			currentCategoryAlias.value = val
		}

		return {
			scrollToTop,
			backToTopObserveElement,
			isShowClose,
			bannerContent,
			displayedBannerTabs,
			currentBannerTab,
			currentSecondaryTabs,
			currentSecondaryTab,
			postCardTextInfo,
			searchbarTextInfo,
			postListTextInfo,
			isLoading,
			currentSearchParameter,
			isHidePSMemberAccess,
			subMenuList,
			interestsList,
			isShowDialogPostDetail,
			DialogType,
			isShow,
			messages,
			postDetailCurrent,
			interestsMap,
			categoryMap,
			warehouseIdMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			productSharingPostPerPage,
			isLoadingPostDetail,
			localCurrencyInfo,
			textService,
			columnCount,
			sortingOptions,
			isCurrentModeDefault,
			currentMode,
			defaultSorting,
			isDemoApi,
			PSUserAvatarSrc,
			userProgress,
			menuStyleClass,
			postListRef,
			defaultAvatar,
			isFocus,
			isMobile,
			shouldOpenFilter,
			filterCount,
			setBannerTab,
			setSecondaryTab,
			toPage,
			toPrivateProfilePage,
			dismiss,
			setPostActions,
			searchFormFocus,
			hideDialogPostDetail,
			openFilter,
			formatWarehouseAlias,
			formatCategoryAlias,
			currentWarehouseAlias,
			currentCategoryAlias,
			sortMode,
			warehouseIdMapForPostList,
			warehouseList,
		}
	},
}).mount('#bsMain')
